import { useEffect, useRef, useState } from "react";

import { type Vehicle, api, uploadBytes } from "../api";
import {
  MAX_SECONDS,
  type RecordedName,
  RecordingTooQuietError,
  type ActiveRecording,
  startRecording,
} from "../nameRecorder";

/**
 * Records the announcer's spoken name for one car.
 *
 * **Hold to record rather than tap-to-start-tap-to-stop.** A car's name takes about a second, and the
 * shortest reliable way to capture one second of speech is to hold a button for it. Tap-to-toggle means
 * two deliberate actions around something faster than the gap between them, and the usual result is a
 * clip with three seconds of room noise on the end.
 *
 * **A beep plays before capture begins.** A phone gives no other signal that the microphone is live, and
 * without it people start talking too early and re-record. It finishes before capture starts, so it is
 * never on the front of the clip — which matters because these are played back over an anthem.
 *
 * **A take is reviewed before it is uploaded.** The whole point is that these are heard on a television
 * across a room, and the first take of a name with a hard consonant is often clipped. Listening costs one
 * tap and saves discovering it mid-jam.
 */
export function NameRecorder({
  teamId,
  vehicle,
  onClose,
  onSaved,
  onError,
}: {
  teamId: string;
  vehicle: Vehicle;
  onClose: () => void;
  onSaved: (vehicle: Vehicle) => void;
  onError: (message: string) => void;
}) {
  const [phase, setPhase] = useState<"idle" | "arming" | "recording" | "review" | "saving">("idle");
  const [take, setTake] = useState<RecordedName | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const context = useRef<AudioContext | null>(null);
  const active = useRef<ActiveRecording | null>(null);
  const previewUrl = useRef<string | null>(null);

  // Revoking matters here rather than being hygiene: somebody re-recording a name they are not happy with
  // will make a dozen of these, each holding a decoded second of audio.
  useEffect(
    () => () => {
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      active.current?.cancel();
      void context.current?.close();
    },
    [],
  );

  useEffect(() => {
    if (phase !== "recording") return;
    const started = Date.now();
    const timer = setInterval(() => setElapsed((Date.now() - started) / 1000), 100);
    return () => clearInterval(timer);
  }, [phase]);

  async function begin() {
    if (phase !== "idle" && phase !== "review") return;
    // Created inside the gesture, because Safari will not let an `AudioContext` start outside one — and
    // reused, because a context per take leaks and eventually stops producing sound.
    context.current ??= new AudioContext();
    setPhase("arming");
    setElapsed(0);
    try {
      active.current = await startRecording(context.current);
      setPhase("recording");
    } catch (error) {
      setPhase("idle");
      onError(
        error instanceof DOMException && error.name === "NotAllowedError"
          ? "This browser blocked the microphone. Allow it in site settings and try again."
          : "Could not start recording on this device.",
      );
    }
  }

  async function end() {
    if (phase !== "recording" || !active.current) return;
    const recording = active.current;
    active.current = null;
    try {
      const result = await recording.stop();
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
      previewUrl.current = URL.createObjectURL(result.blob);
      setTake(result);
      setPhase("review");
    } catch (error) {
      setPhase("idle");
      onError(
        error instanceof RecordingTooQuietError
          ? error.message
          : "Could not process that recording. Try again.",
      );
    }
  }

  async function save() {
    if (!take) return;
    setPhase("saving");
    try {
      const upload = await api.startName(teamId, vehicle.vehicleId, "audio/wav");
      await uploadBytes(upload, take.blob);
      await api.finishName(teamId, vehicle.vehicleId, upload.key, take.seconds);
      // Re-read so the row gets the freshly signed URL rather than a stale one — the same reason the
      // photo flow returns the whole vehicle.
      const listing = await api.vehicles(teamId);
      const updated = listing.vehicles.find((one) => one.vehicleId === vehicle.vehicleId);
      if (updated) onSaved(updated);
      onClose();
    } catch {
      setPhase("review");
      onError("Could not save that recording. Try again.");
    }
  }

  const recorded = vehicle.recordedNameUrl;
  const automatic = vehicle.autoNameUrl;

  return (
    <div className="sheet" role="dialog" aria-label={`Record ${vehicle.name ?? "this car"}`}>
      <div className="sheet-body">
        <p className="section-title">Record “{vehicle.name ?? "this car"}”</p>
        <p className="muted">
          Hold the button and say just the car’s name. A beep plays first — start speaking after it.
          Silence before and after is trimmed off automatically, so there is no need to rush.
        </p>

        {/*
          Both slots, when both exist, because they are not alternatives to choose between: the recording is
          what plays and the computer voice is what it falls back to. Showing them together is what makes
          "Remove" obviously safe — it is visibly a return to the clip below, not a deletion.
        */}
        {phase === "idle" && recorded && (
          <div className="card" style={{ marginTop: 12 }}>
            <p className="muted" style={{ marginBottom: 8 }}>
              Your recording. This is what plays.
            </p>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a spoken car name has no dialogue to caption */}
            <audio controls src={recorded} style={{ width: "100%" }} />
          </div>
        )}

        {phase === "idle" && automatic && (
          <div className="card" style={{ marginTop: 12 }}>
            <p className="muted" style={{ marginBottom: 8 }}>
              {recorded
                ? "Computer voice, kept as a backup if you remove your recording."
                : "Currently the computer voice. Recording your own replaces it."}
            </p>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- as above */}
            <audio controls src={automatic} style={{ width: "100%" }} />
          </div>
        )}

        {phase === "review" && take && (
          <div className="card" style={{ marginTop: 12 }}>
            <p className="muted" style={{ marginBottom: 8 }}>
              {/*
                Reported rather than silent, because the crop is the surprising part: somebody who held the
                button for four seconds and gets back a one-second clip should be able to see that was
                deliberate. Only mentioned when it actually removed something worth mentioning.
              */}
              {take.rawSeconds - take.seconds > 0.25
                ? `${take.seconds.toFixed(1)}s, trimmed from ${take.rawSeconds.toFixed(1)}s and levelled.`
                : `${take.seconds.toFixed(1)}s, levelled to match the announcer.`}{" "}
              Have a listen.
            </p>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- as above */}
            <audio controls autoPlay src={previewUrl.current ?? undefined} style={{ width: "100%" }} />
          </div>
        )}

        {phase === "recording" && (
          <p className="section-title" style={{ marginTop: 12 }}>
            Recording… {elapsed.toFixed(1)}s
            <span className="muted"> / {MAX_SECONDS}s</span>
          </p>
        )}

        <div className="row" style={{ marginTop: 16, gap: 8 }}>
          {phase === "recording" ? (
            <button className="btn primary grow" onClick={() => void end()}>
              Stop
            </button>
          ) : (
            <button
              className="btn primary grow"
              disabled={phase === "arming" || phase === "saving"}
              onClick={() => void begin()}
            >
              {phase === "arming"
                ? "Get ready…"
                : phase === "review"
                  ? "Record again"
                  : recorded
                    ? "Replace recording"
                    : "Record my voice"}
            </button>
          )}
          {phase === "review" && (
            <button className="btn primary grow" onClick={() => void save()}>
              Use this
            </button>
          )}
        </div>

        <div className="row" style={{ marginTop: 8, gap: 8 }}>
          {recorded && phase === "idle" && (
            <button
              className="btn grow"
              onClick={() =>
                void (async () => {
                  try {
                    await api.deleteName(teamId, vehicle.vehicleId);
                    const listing = await api.vehicles(teamId);
                    const updated = listing.vehicles.find(
                      (one) => one.vehicleId === vehicle.vehicleId,
                    );
                    if (updated) onSaved(updated);
                    onClose();
                  } catch {
                    onError("Could not remove that recording.");
                  }
                })()
              }
            >
              {automatic ? "Remove, use computer voice" : "Remove"}
            </button>
          )}
          <button className="btn grow" disabled={phase === "saving"} onClick={onClose}>
            {phase === "saving" ? "Saving…" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
